import type { ModuleDto } from '@app/contracts';
import { useEffect } from 'react';
import { useMySites, useSiteProgress, useToggleTask } from '../../progression/hooks/useProgression';
import { useSiteSelectionStore } from '../../progression/store/site-selection.store';
import {
  useAllModules,
  useAssignModule,
  useSiteModules,
  useUnassignModule,
} from '../hooks/useModules';

const TASK_VALIDATE = 'phase-1.identity.validate';

function ModuleToggleRow({
  module,
  assigned,
  siteId,
}: {
  module: ModuleDto;
  assigned: boolean;
  siteId: string;
}) {
  const assign = useAssignModule(siteId);
  const unassign = useUnassignModule(siteId);
  const isPending = assign.isPending || unassign.isPending;

  function toggle() {
    if (isPending) return;
    if (assigned) {
      unassign.mutate(module.id);
    } else {
      assign.mutate(module.id);
    }
  }

  return (
    <li className="flex items-start gap-4 rounded-2xl border border-slate-100 bg-white p-4 shadow-sm transition-shadow hover:shadow-md">
      <label
        className={`mt-0.5 flex h-6 w-6 shrink-0 cursor-pointer items-center justify-center rounded-lg border-2 transition-colors
          ${
            assigned
              ? 'border-emerald-500 bg-emerald-500 text-white'
              : 'border-slate-300 bg-white text-transparent hover:border-emerald-400'
          }
          ${isPending ? 'cursor-wait opacity-60' : ''}`}
      >
        <input
          type="checkbox"
          className="peer sr-only"
          checked={assigned}
          disabled={isPending}
          onChange={toggle}
          aria-label={`${assigned ? 'Retirer' : 'Ajouter'} ${module.name}`}
        />
        <span className="pointer-events-none flex h-full w-full items-center justify-center rounded-[inherit] peer-focus-visible:outline peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-emerald-400">
          <svg className="h-3.5 w-3.5" viewBox="0 0 14 14" fill="none" aria-hidden>
            <title>Coche du module</title>
            <path
              d="M2 7l4 4 6-6"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </span>
      </label>

      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-semibold text-slate-800 text-[0.92rem]">{module.name}</span>
          <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[0.72rem] font-mono font-medium text-slate-500">
            {module.code}
          </span>
          {assigned && (
            <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[0.72rem] font-semibold text-emerald-700 ring-1 ring-emerald-200">
              Actif
            </span>
          )}
        </div>
        {module.description && (
          <p className="mt-1 text-[0.82rem] text-slate-500 leading-relaxed">{module.description}</p>
        )}
      </div>
    </li>
  );
}

function ValidationSection({ siteId }: { siteId: string }) {
  const progress = useSiteProgress(siteId);
  const toggle = useToggleTask(siteId);

  if (progress.isLoading || !progress.data) return null;

  const { sitePhaseCode, userPhaseCode, completedTaskCodes } = progress.data;
  const taskDone = completedTaskCodes.includes(TASK_VALIDATE);

  // La section est visible dès que le site a atteint la phase-1 (toutes les tâches phase-0 terminées)
  // ou que la tâche a déjà été validée (phase suivante).
  const siteReachedPhase1 = sitePhaseCode !== 'phase-0';
  if (!siteReachedPhase1 && !taskDone) return null;

  const userReachedPhase1 = userPhaseCode !== 'phase-0';
  const isPending = toggle.isPending;

  function handleToggle() {
    toggle.mutate({ taskCode: TASK_VALIDATE, completed: !taskDone });
  }

  return (
    <div
      className={`rounded-2xl border p-5 transition-colors ${
        taskDone ? 'border-emerald-200 bg-emerald-50' : 'border-amber-200 bg-amber-50'
      }`}
    >
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-start gap-3">
          <span
            className={`mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full ${
              taskDone
                ? 'bg-emerald-500 text-white'
                : 'border-2 border-amber-400 bg-white text-transparent'
            }`}
          >
            <svg className="h-3.5 w-3.5" viewBox="0 0 14 14" fill="none" aria-hidden>
              <title>Validation</title>
              <path
                d="M2 7l4 4 6-6"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </span>
          <div>
            <p
              className={`text-[0.92rem] font-semibold ${taskDone ? 'text-emerald-800' : 'text-amber-900'}`}
            >
              Valider l'étape « Identité entreprise »
            </p>
            <p className="mt-0.5 text-[0.8rem] text-slate-600 leading-relaxed">
              {taskDone
                ? 'Vous avez confirmé l\u2019étape identité entreprise pour ce parcours.'
                : 'Confirmez que vous avez complété les informations de votre entreprise et sélectionné vos modules. Un site peut ne pas avoir de module ISO \u2014 la validation reste nécessaire pour progresser.'}
            </p>
          </div>
        </div>

        <div className="flex shrink-0 flex-col items-end gap-2">
          {taskDone ? (
            <>
              <span className="rounded-full bg-emerald-100 px-3 py-1 text-[0.75rem] font-semibold uppercase tracking-wide text-emerald-800 ring-1 ring-emerald-200">
                Étape validée
              </span>
              <button
                type="button"
                disabled={isPending}
                onClick={handleToggle}
                className="text-[0.78rem] text-slate-400 underline underline-offset-2 hover:text-slate-600 disabled:cursor-wait"
              >
                Annuler la validation
              </button>
            </>
          ) : !userReachedPhase1 ? (
            <p className="max-w-[18rem] text-right text-[0.78rem] text-amber-700">
              Avancez d'abord votre phase utilisateur vers Phase 1 depuis le parcours de
              certification.
            </p>
          ) : (
            <button
              type="button"
              disabled={isPending}
              onClick={handleToggle}
              className="btn-primary disabled:cursor-wait disabled:opacity-60"
            >
              {isPending ? 'En cours\u2026' : 'Valider l\u2019\u00e9tape'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export function ModulesPage() {
  const { selectedSiteId, setSelectedSiteId } = useSiteSelectionStore();
  const sites = useMySites();

  useEffect(() => {
    const list = sites.data;
    if (!list || list.length === 0) return;
    const allowed = new Set(list.map((s) => s.siteId));
    if (!selectedSiteId || !allowed.has(selectedSiteId)) {
      const preferred = list.find((s) => s.isPreferred) ?? list[0];
      if (preferred) setSelectedSiteId(preferred.siteId);
    }
  }, [sites.data, selectedSiteId, setSelectedSiteId]);

  const allModules = useAllModules();
  const assignedModulesQuery = useSiteModules(selectedSiteId ?? undefined);

  const assignedIds = new Set((assignedModulesQuery.data ?? []).map((m) => m.id));

  const isLoading = allModules.isLoading || sites.isLoading;
  const hasError = allModules.isError || sites.isError;
  const currentSite = sites.data?.find((s) => s.siteId === selectedSiteId);

  return (
    <div className="mx-auto max-w-3xl space-y-8">
      {/* En-tête */}
      <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900">Mes modules</h1>
          <p className="mt-1 text-[0.88rem] text-slate-500">
            Sélectionnez les certifications ISO que votre site vise à obtenir.
          </p>
        </div>
      </div>

      {/* Bandeau site actif + compteur */}
      {currentSite && (
        <div className="flex items-center gap-2 rounded-xl bg-slate-50 px-4 py-3 ring-1 ring-slate-200">
          <svg
            className="h-4 w-4 shrink-0 text-slate-400"
            viewBox="0 0 20 20"
            fill="currentColor"
            aria-hidden
          >
            <title>Site sélectionné</title>
            <path
              fillRule="evenodd"
              d="M9.69 18.933l.003.001C9.89 19.02 10 19 10 19s.11.02.308-.066l.002-.001.006-.003.018-.008a5.741 5.741 0 00.281-.14c.186-.096.446-.24.757-.433.62-.384 1.445-.966 2.274-1.765C15.302 14.988 17 12.493 17 9A7 7 0 103 9c0 3.492 1.698 5.988 3.355 7.584a13.731 13.731 0 002.273 1.765 11.842 11.842 0 00.765.428l.006.003.018.008.006.003zM10 11.25a2.25 2.25 0 100-4.5 2.25 2.25 0 000 4.5z"
              clipRule="evenodd"
            />
          </svg>
          <span className="text-[0.85rem] font-medium text-slate-700">{currentSite.siteName}</span>
          <span className="ml-auto text-[0.8rem] text-slate-500">
            {assignedIds.size} module{assignedIds.size !== 1 ? 's' : ''} sélectionné
            {assignedIds.size !== 1 ? 's' : ''}
          </span>
        </div>
      )}

      {/* Chargement */}
      {isLoading && (
        <div className="flex items-center justify-center py-16">
          <span className="relative flex h-10 w-10">
            <span className="absolute inset-0 rounded-full border-[3px] border-slate-200" />
            <span className="absolute inset-0 animate-spin rounded-full border-[3px] border-transparent border-t-emerald-500" />
          </span>
        </div>
      )}

      {hasError && (
        <div className="rounded-2xl bg-red-50 p-5 text-[0.88rem] text-red-700 ring-1 ring-red-200">
          Impossible de charger les modules. Veuillez réessayer.
        </div>
      )}

      {!isLoading && !hasError && !selectedSiteId && (
        <div className="rounded-2xl bg-slate-50 p-8 text-center text-[0.88rem] text-slate-500 ring-1 ring-slate-200">
          Aucun site trouvé. Créez d'abord un site dans{' '}
          <strong className="font-semibold text-slate-700">Mon entreprise › Gérer les sites</strong>
          .
        </div>
      )}

      {!isLoading && !hasError && selectedSiteId && allModules.data && (
        <>
          {/* Liste des modules */}
          <ul className="space-y-3">
            {allModules.data.map((module) => (
              <ModuleToggleRow
                key={module.id}
                module={module}
                assigned={assignedIds.has(module.id)}
                siteId={selectedSiteId}
              />
            ))}
          </ul>

          {/* Validation de l'étape Phase 1 */}
          <ValidationSection siteId={selectedSiteId} />
        </>
      )}
    </div>
  );
}
